import { Injectable, Inject, InjectionToken } from '@angular/core';
import { NetworkService, API_BASE_URL } from '@angular-workspace/core';
import { COMMUNITY_BASE_URL } from './discussion-api.service';

/**
 * Injection token for challenge base URL
 */
export const CHALLENGE_BASE_URL = new InjectionToken<string>('CHALLENGE_BASE_URL');

/**
 * Challenge API Service for handling challenge-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class ChallengeApiService {
  constructor(
    private networkService: NetworkService,
    @Inject(API_BASE_URL) private basePathUrl: string,
    @Inject(CHALLENGE_BASE_URL) private challengeBaseUrl: string,
    @Inject(COMMUNITY_BASE_URL) private communityBaseUrl: string,
  ) {}

  private getCommunityChallengeBase(): string {
    return this.communityBaseUrl.endsWith('/')
      ? this.communityBaseUrl.slice(0, -1)
      : this.communityBaseUrl;
  }

  /**
   * Get evaluation challenges
   * @param payload Pagination and sort parameters
   * @returns Promise with evaluation challenges list
   */
  async getEvaluationChallenges(payload: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
    query?: string;
  }): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('page', (payload.page || 1).toString());
      params.append('limit', (payload.limit || 10).toString());
      if (payload.sort_by) {
        params.append('sort_by', payload.sort_by);
      }
      if (payload.sort_order) {
        params.append('sort_order', payload.sort_order);
      }
      if (payload.query != null && payload.query.trim() !== '') {
        params.append('query', payload.query.trim());
      }

      // Use community base URL (env.baseCommunityPathUrl)
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/challenge/admin/challenges/evaluation?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching evaluation challenges:', error);
      throw error;
    }
  }

  /**
   * Get completed challenges
   * @param payload Pagination and sort parameters
   * @returns Promise with completed challenges list
   */
  async getCompletedChallenges(payload: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
    query?: string;
  }): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('page', (payload.page || 1).toString());
      params.append('limit', (payload.limit || 10).toString());
      if (payload.sort_by) {
        params.append('sort_by', payload.sort_by);
      }
      if (payload.sort_order) {
        params.append('sort_order', payload.sort_order);
      }
      if (payload.query != null && payload.query.trim() !== '') {
        params.append('query', payload.query.trim());
      }

      // Use community base URL (env.baseCommunityPathUrl)
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/challenge/admin/challenges/completed?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching completed challenges:', error);
      throw error;
    }
  }

  /**
   * Delete a draft challenge (My Drafts modal).
   * DELETE /challenge/admin/challenges/<challengeId>
   * @param challengeId The challenge ID to delete
   * @returns Promise with response (e.g. success, message, data.competition_id)
   */
  async deleteMyDrafts(challengeId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/admin/challenges/${encodeURIComponent(challengeId)}`;

      const response = await this.networkService.delete_api(
        url,
        {},
        { 'Content-Type': 'application/json' }
      );

      return response;
    } catch (error) {
      console.error('Error deleting draft challenge:', error);
      throw error;
    }
  }

  /**
   * Delete a scheduled challenge (admin).
   * DELETE /challenge/admin/challenges/<challengeId>
   * @param challengeId The challenge ID to delete
   * @returns Promise with response (e.g. success, message, data.competition_id)
   */
  async deleteScheduleChallenge(challengeId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/admin/challenges/${encodeURIComponent(challengeId)}`;

      const response = await this.networkService.delete_api(
        url,
        {},
        { 'Content-Type': 'application/json' }
      );

      return response;
    } catch (error) {
      console.error('Error deleting scheduled challenge:', error);
      throw error;
    }
  }

  /**
   * Get scheduled challenges
   * @param payload Pagination and sort parameters
   * @returns Promise with scheduled challenges list
   */
  async getScheduledChallenges(payload: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
    query?: string;
  }): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('page', (payload.page || 1).toString());
      params.append('limit', (payload.limit || 10).toString());
      if (payload.sort_by) {
        params.append('sort_by', payload.sort_by);
      }
      if (payload.sort_order) {
        params.append('sort_order', payload.sort_order);
      }
      if (payload.query != null && payload.query.trim() !== '') {
        params.append('query', payload.query.trim());
      }

      // Use community base URL (env.baseCommunityPathUrl), same path pattern as draft
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/challenge/admin/challenges/scheduled?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching scheduled challenges:', error);
      throw error;
    }
  }

  /**
   * Get draft challenges (admin).
   * Called when "My Drafts" is clicked on platform admin Create Challenge page.
   * GET /challenge/admin/challenges/draft?page=1&limit=10&sort_by=updated_at&sort_order=desc
   * @param payload Pagination and sort parameters (e.g. page=1, limit=10, sort_by=updated_at, sort_order=desc)
   * @returns Promise with draft challenges list (response.data or response.data.competitions)
   */
  async getDraftChallenges(payload: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('page', (payload.page || 1).toString());
      params.append('limit', (payload.limit || 10).toString());
      if (payload.sort_by) {
        params.append('sort_by', payload.sort_by);
      }
      if (payload.sort_order) {
        params.append('sort_order', payload.sort_order);
      }

      // Use community base URL for drafts API
      const baseUrl = this.communityBaseUrl.endsWith('/') 
        ? this.communityBaseUrl.slice(0, -1) 
        : this.communityBaseUrl;
      
      const url = `${baseUrl}/challenge/admin/challenges/draft?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching draft challenges:', error);
      throw error;
    }
  }

  /**
   * Get published/running challenges (Running Challenges tab).
   * Uses env.baseCommunityPathUrl (COMMUNITY_BASE_URL), not baseChallengePathUrl.
   * @param payload Pagination and sort parameters
   * @returns Promise with published challenges list
   */
  async getPublishedChallenges(payload: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
    query?: string;
  }): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('page', (payload.page || 1).toString());
      params.append('limit', (payload.limit || 10).toString());
      if (payload.sort_by) {
        params.append('sort_by', payload.sort_by);
      }
      if (payload.sort_order) {
        params.append('sort_order', payload.sort_order);
      }
      if (payload.query != null && payload.query.trim() !== '') {
        params.append('query', payload.query.trim());
      }

      // Running challenges: must use community base URL (env.baseCommunityPathUrl)
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/challenge/admin/challenges/published?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching published challenges:', error);
      throw error;
    }
  }

  /**
   * Get new/published challenges (public endpoint)
   * @param payload Pagination, sort, and optional search query (defaults: page=1, limit=6, sort_by=Newest)
   * @returns Promise with published challenges list
   */
  async newChallenges(payload?: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
    query?: string;
  }): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('page', ((payload?.page) ?? 1).toString());
      params.append('limit', ((payload?.limit) ?? 6).toString());
      params.append('sort_by', payload?.sort_by ?? 'Newest');
      if (payload?.sort_order) {
        params.append('sort_order', payload.sort_order);
      }
      if (payload?.query != null && payload.query.trim() !== '') {
        params.append('query', payload.query.trim());
      }

      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/challenge/published?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false
      );

      return response;
    } catch (error) {
      console.error('Error fetching new challenges:', error);
      throw error;
    }
  }

  /**
   * Get evaluation challenges (public endpoint)
   * @param payload Pagination and sort parameters (defaults: page=1, limit=6, sort_by=Newest)
   * @returns Promise with evaluation challenges list
   */
  async evaluationChallenge(payload?: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
    query?: string;
  }): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('page', ((payload?.page) ?? 1).toString());
      params.append('limit', ((payload?.limit) ?? 6).toString());
      params.append('sort_by', payload?.sort_by ?? 'Newest');
      if (payload?.sort_order) {
        params.append('sort_order', payload.sort_order);
      }
      if (payload?.query != null && payload.query.trim() !== '') {
        params.append('query', payload.query.trim());
      }

      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/challenge/evaluation?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false
      );

      return response;
    } catch (error) {
      console.error('Error fetching evaluation challenges:', error);
      throw error;
    }
  }

  /**
   * Get a single challenge for admin (draft edit flow) — same path as {@link updateChallenge} (PUT).
   * GET /challenge/admin/challenge/<challengeId>
   * Returns payload-shaped fields (e.g. dataset_description, data_models, rules_and_guidelines) suitable for prefilling the create form.
   */
  async getAdminChallengeById(challengeId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/admin/challenge/${encodeURIComponent(challengeId)}`;

      const response = await this.networkService.get_api(url, undefined, false);

      return response;
    } catch (error) {
      console.error('Error fetching admin challenge by ID:', error);
      throw error;
    }
  }

  /**
   * Get a single challenge by ID (public endpoint)
   * GET /challenge/by_id/<challengeId>
   * @param challengeId The challenge ID
   * @returns Promise with the challenge details
   */
  async getChallengeById(challengeId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/by_id/${encodeURIComponent(challengeId)}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false
      );

      return response;
    } catch (error) {
      console.error('Error fetching challenge by ID:', error);
      throw error;
    }
  }

  /**
   * Get rules and guidelines download URL for a challenge
   * GET /challenge/attachment/download/rules_and_guidelines/<challengeId>
   * @param challengeId The challenge ID
   * @returns Promise with response containing download_url
   */
  async downloadChallengeRulesAndGuideline(challengeId: string): Promise<{ download_url: string }> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/attachment/download/rules_and_guidelines/${encodeURIComponent(challengeId)}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false
      );

      return response;
    } catch (error) {
      console.error('Error fetching challenge rules and guideline download URL:', error);
      throw error;
    }
  }

  /**
   * Get solution download URLs for a submission (Challenges Submitted > Solution download)
   * GET /challenge/attachment/download/submission/<submissionId>/solution
   * @param submissionId The submission ID
   * @returns Promise with response containing data.download_urls (array of presigned URLs)
   */
  async getChallengeSolution(submissionId: string): Promise<{ data?: { download_urls?: string[] } }> {
    return this.getSubmissionDownloadUrls(submissionId, 'solution');
  }

  /**
   * Get presigned download URLs for a submission (admin or user).
   * GET /challenge/attachment/download/submission/<submissionId>/<choice>
   */
  async getSubmissionDownloadUrls(
    submissionId: string,
    choice: 'solution' | 'evaluation' = 'solution',
  ): Promise<{
    success?: boolean;
    message?: string;
    data?: { download_urls?: string[] };
  }> {
    try {
      const baseUrl = this.getCommunityChallengeBase();
      const url = `${baseUrl}/challenge/attachment/download/submission/${encodeURIComponent(submissionId)}/${choice}`;

      const response = await this.networkService.get_api(url, undefined, false);

      return response;
    } catch (error) {
      console.error('Error fetching submission download URLs:', error);
      throw error;
    }
  }

  /**
   * List submissions for a challenge (platform admin).
   * GET /challenge/admin/challenges/<competitionId>/submissions
   */
  async getAdminChallengeSubmissions(
    competitionId: string,
    payload?: {
      page?: number;
      limit?: number;
      query?: string;
      sort_by?: string;
      sort_order?: 'asc' | 'desc';
    },
  ): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('page', String(payload?.page ?? 1));
      params.append('limit', String(payload?.limit ?? 10));
      if (payload?.query?.trim()) {
        params.append('query', payload.query.trim());
      }
      if (payload?.sort_by) {
        params.append('sort_by', payload.sort_by);
      }
      if (payload?.sort_order) {
        params.append('sort_order', payload.sort_order);
      }

      const baseUrl = this.getCommunityChallengeBase();
      const url = `${baseUrl}/challenge/admin/challenges/${encodeURIComponent(competitionId)}/submissions?${params.toString()}`;

      return await this.networkService.get_api(url, undefined, false);
    } catch (error) {
      console.error('Error fetching admin challenge submissions:', error);
      throw error;
    }
  }

  /**
   * Evaluate or score a submission (platform admin).
   * PUT /challenge/admin/submission/<submissionId>/evaluate
   */
  async evaluateSubmission(
    submissionId: string,
    payload: {
      score: number;
      comments: string;
      disqualify?: boolean;
      attachments?: { add?: string[]; remove?: string[] };
    },
  ): Promise<any> {
    try {
      const baseUrl = this.getCommunityChallengeBase();
      const url = `${baseUrl}/challenge/admin/submission/${encodeURIComponent(submissionId)}/evaluate`;

      return await this.networkService.put_api(
        url,
        payload,
        { 'Content-Type': 'application/json' },
      );
    } catch (error) {
      console.error('Error evaluating submission:', error);
      throw error;
    }
  }

  /**
   * Disqualify a submission (platform admin).
   * PUT /challenge/admin/submission/<submissionId>/evaluate with disqualify flag.
   */
  async disqualifySubmission(
    submissionId: string,
    payload: {
      comments: string;
      attachments?: { add?: string[]; remove?: string[] };
    },
  ): Promise<any> {
    return this.evaluateSubmission(submissionId, {
      score: 0,
      comments: payload.comments,
      disqualify: true,
      attachments: payload.attachments,
    });
  }

  /**
   * Announce challenge results / winners (platform admin).
   * POST /challenge/admin/challenges/announce-result?competition_id=<id>
   */
  async announceChallengeResult(competitionId: string): Promise<any> {
    try {
      const baseUrl = this.getCommunityChallengeBase();
      const url = `${baseUrl}/challenge/admin/challenges/announce-result?competition_id=${encodeURIComponent(competitionId)}`;

      return await this.networkService.post_api(url, {}, { 'Content-Type': 'application/json' }, false);
    } catch (error) {
      console.error('Error announcing challenge result:', error);
      throw error;
    }
  }

  /**
   * Get current user's bookmarked challenges (public endpoint)
   * GET /challenge/bookmarked?page=1&limit=9&sort_by=Newest
   * @param payload Pagination and sort parameters (defaults: page=1, limit=9, sort_by=Newest)
   * @returns Promise with bookmarked challenges list
   */
  async listBookmark(payload?: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('page', ((payload?.page) ?? 1).toString());
      params.append('limit', ((payload?.limit) ?? 9).toString());
      params.append('sort_by', payload?.sort_by ?? 'Newest');
      if (payload?.sort_order) {
        params.append('sort_order', payload.sort_order);
      }

      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/challenge/bookmarked?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false
      );

      return response;
    } catch (error) {
      console.error('Error fetching bookmarked challenges:', error);
      throw error;
    }
  }

  /**
   * Get challenges the user has participated in (interested/joined) - for Challenges Submitted > Interested tab
   * GET /challenge/participated?page=1&limit=10&sort_by=submission_starts_at&sort_order=desc
   */
  async getParticipatedChallenges(payload?: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('page', ((payload?.page) ?? 1).toString());
      params.append('limit', ((payload?.limit) ?? 10).toString());
      params.append('sort_by', payload?.sort_by ?? 'submission_starts_at');
      if (payload?.sort_order) {
        params.append('sort_order', payload.sort_order);
      }
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/participated?${params.toString()}`;
      const response = await this.networkService.get_api(url, undefined, false);
      return response;
    } catch (error) {
      console.error('Error fetching participated challenges:', error);
      throw error;
    }
  }

  /**
   * Get user's submitted submissions - for Challenges Submitted > Submitted tab
   * GET /challenge/submissions/submitted?page=1&limit=10&sort_by=created_at&sort_order=desc
   */
  async getSubmittedSubmissions(payload?: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('page', ((payload?.page) ?? 1).toString());
      params.append('limit', ((payload?.limit) ?? 10).toString());
      params.append('sort_by', payload?.sort_by ?? 'created_at');
      if (payload?.sort_order) {
        params.append('sort_order', payload.sort_order);
      }
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/submissions/submitted?${params.toString()}`;
      const response = await this.networkService.get_api(url, undefined, false);
      return response;
    } catch (error) {
      console.error('Error fetching submitted submissions:', error);
      throw error;
    }
  }

  /**
   * Get user's submissions in evaluation - for Challenges Submitted > In Evaluation tab
   * GET /challenge/submissions/evaluation?page=1&limit=10&sort_by=evaluation_ends_at&sort_order=desc
   */
  async getEvaluationSubmissions(payload?: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('page', ((payload?.page) ?? 1).toString());
      params.append('limit', ((payload?.limit) ?? 10).toString());
      params.append('sort_by', payload?.sort_by ?? 'evaluation_ends_at');
      if (payload?.sort_order) {
        params.append('sort_order', payload.sort_order);
      }
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/submissions/evaluation?${params.toString()}`;
      const response = await this.networkService.get_api(url, undefined, false);
      return response;
    } catch (error) {
      console.error('Error fetching evaluation submissions:', error);
      throw error;
    }
  }

  /**
   * Get user's completed submissions - for Challenges Submitted > Completed tab
   * GET /challenge/submissions/completed?page=1&limit=10&sort_by=created_at&sort_order=desc
   */
  async getCompletedSubmissions(payload?: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('page', ((payload?.page) ?? 1).toString());
      params.append('limit', ((payload?.limit) ?? 10).toString());
      params.append('sort_by', payload?.sort_by ?? 'created_at');
      if (payload?.sort_order) {
        params.append('sort_order', payload.sort_order);
      }
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/submissions/completed?${params.toString()}`;
      const response = await this.networkService.get_api(url, undefined, false);
      return response;
    } catch (error) {
      console.error('Error fetching completed submissions:', error);
      throw error;
    }
  }

  /**
   * Get past/completed challenges (public endpoint)
   * @param payload Pagination and sort parameters (defaults: page=1, limit=6, sort_by=Newest)
   * @returns Promise with completed challenges list
   */
  async pastChallenge(payload?: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
    query?: string;
  }): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('page', ((payload?.page) ?? 1).toString());
      params.append('limit', ((payload?.limit) ?? 6).toString());
      params.append('sort_by', payload?.sort_by ?? 'Newest');
      if (payload?.sort_order) {
        params.append('sort_order', payload.sort_order);
      }
      if (payload?.query != null && payload.query.trim() !== '') {
        params.append('query', payload.query.trim());
      }

      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;

      const url = `${baseUrl}/challenge/completed?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false
      );

      return response;
    } catch (error) {
      console.error('Error fetching past challenges:', error);
      throw error;
    }
  }

  /**
   * Upload a challenge attachment (get presigned URL).
   * POST /challenge/attachment
   * @param payload batch_id, file_name, and optional md_attachment (default false)
   * @returns Promise with response containing data.presigned_url (and optionally data.url, meta.object_key)
   */
  async uploadChallengeAttachment(payload: {
    batch_id: string;
    file_name: string;
    md_attachment?: boolean;
  }): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/attachment`;

      const body = {
        batch_id: payload.batch_id,
        file_name: payload.file_name,
        md_attachment: payload.md_attachment ?? false
      };
      const response = await this.networkService.post_api(
        url,
        body,
        { 'Content-Type': 'application/json' },
        false
      );

      return response;
    } catch (error) {
      console.error('Error uploading challenge attachment:', error);
      throw error;
    }
  }

  /**
   * Delete a challenge attachment (before submitting the solution)
   * DELETE /challenge/attachment
   * @param payload batch_id and object_key from the upload response's meta object (meta.batch_id, meta.object_key)
   * @returns Promise with the delete response
   */
  async deleteChallengeAttachment(payload: {
    batch_id: string;
    object_key: string;
  }): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/attachment`;

      const response = await this.networkService.delete_api(
        url,
        payload,
        { 'Content-Type': 'application/json' }
      );

      return response;
    } catch (error) {
      console.error('Error deleting challenge attachment:', error);
      throw error;
    }
  }

  /**
   * Resubmit/update a solution for a challenge (when user has already submitted).
   * PUT /challenge/submission/<submissionId>
   * @param submissionId The submission ID to update
   * @param payload title, description, and attachments with add (new s3_keys) and remove (s3_keys to remove)
   * @returns Promise with the submission response
   */
  async resubmitChallenge(
    submissionId: string,
    payload: {
      title: string;
      description: string;
      attachments: { add: string[]; remove: string[] };
    }
  ): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/submission/${encodeURIComponent(submissionId)}`;

      const response = await this.networkService.put_api(
        url,
        payload,
        { 'Content-Type': 'application/json' }
      );

      return response;
    } catch (error) {
      console.error('Error resubmitting challenge solution:', error);
      throw error;
    }
  }

  /**
   * Submit a solution for a challenge
   * POST /challenge/<challengeId>/submission
   * @param challengeId The challenge ID to submit to
   * @param payload title, description, and attachments (array of object_key strings from upload)
   * @returns Promise with the submission response
   */
  async submitChallenge(
    challengeId: string,
    payload: { title: string; description: string; attachments: string[] }
  ): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/${encodeURIComponent(challengeId)}/submission`;

      const response = await this.networkService.post_api(
        url,
        payload,
        { 'Content-Type': 'application/json' },
        false
      );

      return response;
    } catch (error) {
      console.error('Error submitting challenge solution:', error);
      throw error;
    }
  }

  /**
   * Join a challenge
   * POST /challenge/users/challenges/<challengeId>/join
   * @param challengeId The challenge ID to join
   * @returns Promise with the join response
   */
  async joinChallenge(challengeId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/users/challenges/${encodeURIComponent(challengeId)}/join`;

      const response = await this.networkService.post_api(
        url,
        {},
        { 'Content-Type': 'application/json' },
        false
      );

      return response;
    } catch (error) {
      console.error('Error joining challenge:', error);
      throw error;
    }
  }

  /**
   * Bookmark a challenge (call when is_bookmark is false)
   * POST /challenge/users/challenges/<challengeId>/bookmark
   * @param challengeId The challenge ID to bookmark
   * @returns Promise with the bookmark response
   */
  async bookmarkChallenge(challengeId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/users/challenges/${encodeURIComponent(challengeId)}/bookmark`;

      const response = await this.networkService.post_api(
        url,
        {},
        { 'Content-Type': 'application/json' },
        false
      );

      return response;
    } catch (error) {
      console.error('Error bookmarking challenge:', error);
      throw error;
    }
  }

  /**
   * Remove bookmark from a challenge (call when is_bookmark is true)
   * DELETE /challenge/users/challenges/<challengeId>/bookmark
   * @param challengeId The challenge ID to unbookmark
   * @returns Promise with the delete bookmark response
   */
  async deleteBookmarkChallenge(challengeId: string): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/users/challenges/${encodeURIComponent(challengeId)}/bookmark`;

      const response = await this.networkService.delete_api(
        url,
        {},
        { 'Content-Type': 'application/json' }
      );

      return response;
    } catch (error) {
      console.error('Error deleting challenge bookmark:', error);
      throw error;
    }
  }

  /**
   * Get current user's submissions for a challenge (for resubmit pre-fill)
   * GET /challenge/users/challenges/<challengeId>/submissions?page=1&limit=1
   * @param challengeId The challenge ID
   * @param page Page number (default 1)
   * @param limit Page size (default 1)
   * @returns Promise with submissions list (array of submission objects)
   */
  async getUserSubmissions(
    challengeId: string,
    page: number = 1,
    limit: number = 1
  ): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', String(limit));
      const url = `${baseUrl}/challenge/users/challenges/${encodeURIComponent(challengeId)}/submissions?${params.toString()}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false
      );

      return response;
    } catch (error) {
      console.error('Error fetching user submissions:', error);
      throw error;
    }
  }

  /**
   * Create a challenge (draft or published).
   * POST /challenge/admin/challenge
   * @param payload Challenge data (draft, title, subtitle, overview, description, image_url, constraints, currency, evaluation_criteria_definition, submission_file_definition, other_resources, etc.)
   * @returns Promise with the created challenge response
   */
  async createChallenge(payload: {
    draft?: boolean;
    title?: string;
    subtitle?: string;
    overview?: string;
    description?: string;
    image_url?: string;
    constraints?: string;
    currency?: string;
    evaluation_criteria_definition?: string;
    submission_file_definition?: string;
    other_resources?: string;
    [key: string]: unknown;
  }): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/admin/challenge`;

      const response = await this.networkService.post_api(
        url,
        payload,
        { 'Content-Type': 'application/json' },
        false
      );

      return response;
    } catch (error) {
      console.error('Error creating challenge:', error);
      throw error;
    }
  }

  /**
   * Update an existing challenge.
   * PUT /challenge/admin/challenge/<challengeId>
   * @param challengeId The challenge ID to update
   * @param payload Challenge data (draft, title, subtitle, overview, description, constraints, prize_type, currency, etc.)
   * @returns Promise with the updated challenge response
   */
  async updateChallenge(challengeId: string, payload: Record<string, unknown>): Promise<any> {
    try {
      const baseUrl = this.communityBaseUrl.endsWith('/')
        ? this.communityBaseUrl.slice(0, -1)
        : this.communityBaseUrl;
      const url = `${baseUrl}/challenge/admin/challenge/${encodeURIComponent(challengeId)}`;

      const response = await this.networkService.put_api(
        url,
        payload,
        { 'Content-Type': 'application/json' }
      );

      return response;
    } catch (error) {
      console.error('Error updating challenge:', error);
      throw error;
    }
  }

  /**
   * Create or update a challenge (draft)
   * @param challengeId The challenge ID (if updating) or null (if creating new)
   * @param payload The challenge data payload
   * @returns Promise with the created/updated challenge
   */
  async createOrUpdateChallenge(
    challengeId: string | null,
    payload: any
  ): Promise<any> {
    try {
      // Use community base URL for challenge operations
      const baseUrl = this.communityBaseUrl.endsWith('/') 
        ? this.communityBaseUrl.slice(0, -1) 
        : this.communityBaseUrl;
      
      // Use PUT for both create and update operations
      if (challengeId) {
        // Update existing challenge using PUT
        const url = `${baseUrl}/challenge/admin/challenge/${challengeId}`;
        const response = await this.networkService.put_api(
          url,
          payload,
          {
            'Content-Type': 'application/json'
          }
        );
        return response;
      } else {
        // Create new challenge using PUT (without ID, backend will generate one)
        // Note: If backend requires an ID for PUT, you may need to generate a UUID first
        const url = `${baseUrl}/challenge/admin/challenge`;
        const response = await this.networkService.put_api(
          url,
          payload,
          {
            'Content-Type': 'application/json'
          }
        );
        return response;
      }
    } catch (error) {
      console.error('Error creating/updating challenge:', error);
      throw error;
    }
  }
}
